import type { Kpi, Locale } from '../types';
import { chrome } from '../i18n';
import { attainment, ragFor } from '../core/targets';
import { icon } from './Icon';

const TREND_ICON = { up: 'trending-up', down: 'trending-down', flat: 'minus' } as const;

export function KpiGrid(kpis: Kpi[], locale: Locale): string {
  const c = chrome(locale);
  const ragLabel = { achieved: c.onTrack, near: c.nearTarget, off: c.offTarget } as const;
  const dirMeta = {
    up: { ic: 'trending-up', label: c.betterHigh },
    down: { ic: 'trending-down', label: c.betterLow },
    neutral: { ic: 'minus', label: c.betterNeutral },
  } as const;

  const cards = kpis
    .map((k) => {
      const clickable = k.recommendations?.length
        ? `data-rec="kpi:${k.id}" role="button" title="${c.recHint}"`
        : '';

      const info = k.desc
        ? `<span class="info-tip"><span class="info-ic">${icon('info', 'icon icon-xs')}</span><span class="tip"><b>${c.whatItMeans}</b> ${k.desc}</span></span>`
        : '';

      const dir = k.direction ? dirMeta[k.direction] : undefined;
      const dirBadge = dir
        ? `<span class="dir-badge ${k.direction}">${icon(dir.ic, 'icon icon-xs')} ${dir.label}</span>`
        : '';

      let targetRow = '';
      const pct = attainment(k);
      if (k.target && pct !== undefined) {
        const rag = ragFor(pct);
        targetRow = `
      <div class="target ${rag}" title="${ragLabel[rag]} — ${pct}%">
        <div class="t-bar"><span style="width:${Math.min(pct, 100)}%"></span></div>
        <span class="t-label">${c.targetLabel}: ${k.target.label}</span>
      </div>`;
      }

      return `
    <div class="card kpi ${k.recommendations?.length ? 'clickable' : ''}" style="--accent:${k.accent}" tabindex="0" ${clickable} aria-label="${k.label}: ${k.value} ${k.unit}${k.desc ? ` — ${k.desc}` : ''}">
      <div class="label">
        <span class="ic" style="background:${k.accent}22;color:${k.accent}">${icon(k.icon, 'icon icon-kpi')}</span>
        <span class="label-text">${k.label}</span>
        ${info}
        ${k.recommendations?.length ? `<span class="rec-badge">${icon('lightbulb', 'icon icon-xs')}</span>` : ''}
      </div>
      <div><span class="val">${k.value}</span> <span class="unit">${k.unit}</span></div>
      <div class="kpi-foot">
        <span class="delta ${k.sentiment}" title="${c.deltaHint}">${icon(TREND_ICON[k.trend], 'icon icon-xs')} ${k.delta}</span>
        ${dirBadge}
      </div>
      ${targetRow}
    </div>`;
    })
    .join('');
  return `<section class="grid kpis">${cards}</section>`;
}
